import type { Attribute } from '@tiptap/core';
import type { CSSProperties } from 'react';

export const INLINE_IMAGE_NODE_NAME = 'image';

// Marks the side panel, where library images are picked to insert into the active editor. Clicks there (opening the
// uploads tab, then picking an image) must not end table cell editing, as they don't end text block editing.
export const INLINE_IMAGE_SOURCE_CLASS = 'inline-image-source';

export type InlineImageAttrs = {
  src: string | null;
  alt: string | null;
  title: string | null;
  width: number | null;
  height: number | null;
  lockAspectRatio: boolean;
  opacity: number;
  borderWidth: number;
  borderColor: string | null;
  borderRadius: number;
};

const parseNumber = (value: string | null): number | null => {
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

// Every image setting is read from, and written to, its own HTML attribute. The inline style is derived
// from these attributes for display only and is never parsed back, so this markup is the contract for
// anything producing image HTML for the editor.
export const numberAttribute = (name: keyof InlineImageAttrs, htmlAttribute: string, defaultValue: number | null): Attribute => ({
  default: defaultValue,
  parseHTML: (element) => parseNumber(element.getAttribute(htmlAttribute)) ?? defaultValue,
  renderHTML: (attributes) => {
    const value = attributes[name];
    return value === null || value === defaultValue ? {} : { [htmlAttribute]: String(value) };
  },
});

// max-width + aspect-ratio keep the image proportional when its text block or table column is narrower than it.
export const getImageSizeStyle = ({ width, height }: InlineImageAttrs): CSSProperties => ({
  maxWidth: '100%',
  ...(width && { width: `${width}px` }),
  ...(width && height && { aspectRatio: `${width} / ${height}`, height: 'auto' }),
});

export const getImageAppearanceStyle = ({ opacity, borderWidth, borderColor, borderRadius }: InlineImageAttrs): CSSProperties => ({
  boxSizing: 'border-box',
  ...(opacity !== 100 && { opacity: opacity / 100 }),
  ...(borderWidth > 0 && { border: `${borderWidth}px solid ${borderColor ?? 'currentColor'}` }),
  ...(borderRadius > 0 && { borderRadius: `${borderRadius}px` }),
});

export const toStyleString = (style: CSSProperties): string =>
  Object.entries(style)
    .map(([property, value]) => `${property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}: ${value}`)
    .join('; ');
